import {
  collection,
  onSnapshot,
  query,
  Timestamp,
  where,
  type DocumentData,
  type Unsubscribe,
} from 'firebase/firestore';
import { useEffect, useState } from 'react';
import type {
  AiVerificationAssignmentSet,
  AiVerificationDistribution,
  AiVerificationGenerationConstraints,
  AiVerificationRandomConstraints,
} from '@citizen-walk/shared-types';
import { db } from '../../lib/firebase';

/**
 * status='published' な AssignmentSet を地区から subscribe する hook (Sprint V、user 側初実装)。
 *
 * - query: `where('status', '==', 'published')` (closed / draft は除外)
 * - archivedAt フィールドは現状未使用だが、将来追加された場合 client-side で除外
 * - sessionIds と currentSessionId のマッチで client-side filter:
 *     - set.sessionIds が空 / undefined → 地区全体対象、現セッション無関係に表示
 *     - set.sessionIds が非空 → currentSessionId が含まれていれば表示
 *
 * docs/04 §4-3 (AiVerificationAssignmentSet)、REQ-4.3.20〜25。
 */

export type AssignmentSetsState =
  | { status: 'loading'; items: []; error: null }
  | { status: 'ready'; items: AiVerificationAssignmentSet[]; error: null }
  | { status: 'error'; items: []; error: Error };

function tsToIso(value: unknown): string {
  if (value instanceof Timestamp) return value.toDate().toISOString();
  if (typeof value === 'string') return value;
  if (value && typeof value === 'object' && 'toDate' in value) {
    try {
      return (value as { toDate(): Date }).toDate().toISOString();
    } catch {
      return new Date().toISOString();
    }
  }
  return new Date().toISOString();
}

function asDistribution(value: unknown): AiVerificationDistribution {
  if (
    value === 'all_same' ||
    value === 'random_per_user' ||
    value === 'manual' ||
    value === 'free'
  ) {
    return value;
  }
  return 'all_same';
}

function asRandomConstraints(value: unknown): AiVerificationRandomConstraints | undefined {
  if (!value || typeof value !== 'object') return undefined;
  const v = value as Record<string, unknown>;
  if (typeof v.minPostsPerUser !== 'number') return undefined;
  return {
    minPostsPerUser: v.minPostsPerUser,
    minOwnPosts: typeof v.minOwnPosts === 'number' ? v.minOwnPosts : 0,
    allowDuplicates: typeof v.allowDuplicates === 'boolean' ? v.allowDuplicates : false,
    preferOwnPosts: typeof v.preferOwnPosts === 'boolean' ? v.preferOwnPosts : true,
    preferLessVerified: typeof v.preferLessVerified === 'boolean' ? v.preferLessVerified : false,
  };
}

function asPerUserAssignments(value: unknown): Record<string, string[]> | undefined {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return undefined;
  const out: Record<string, string[]> = {};
  for (const [k, v] of Object.entries(value as Record<string, unknown>)) {
    if (Array.isArray(v)) {
      out[k] = v.filter((x): x is string => typeof x === 'string');
    }
  }
  return out;
}

/** generationConstraints (生成時制約) の defensive narrow。object でなければ undefined。 */
function asGenerationConstraints(value: unknown): AiVerificationGenerationConstraints | undefined {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return undefined;
  const v = value as Record<string, unknown>;
  const out: AiVerificationGenerationConstraints = { excludeOwnPosts: v.excludeOwnPosts === true };
  if (typeof v.maxPerUser === 'number') out.maxPerUser = v.maxPerUser;
  if (typeof v.minOwnPosts === 'number') out.minOwnPosts = v.minOwnPosts;
  if (typeof v.preferOwnPosts === 'boolean') out.preferOwnPosts = v.preferOwnPosts;
  if (typeof v.sameSessionOnly === 'boolean') out.sameSessionOnly = v.sameSessionOnly;
  if (
    v.strategyOriginal === 'random_per_user' ||
    v.strategyOriginal === 'round_robin' ||
    v.strategyOriginal === 'all_users'
  ) {
    out.strategyOriginal = v.strategyOriginal;
  }
  return out;
}

/** Firestore doc → AiVerificationAssignmentSet (defensive narrow)。 */
export function mapAssignmentSet(snap: {
  id: string;
  data: () => DocumentData;
}): AiVerificationAssignmentSet {
  const data = snap.data();
  const out: AiVerificationAssignmentSet = {
    id: snap.id,
    displayName: typeof data.displayName === 'string' ? data.displayName : '',
    description: typeof data.description === 'string' ? data.description : '',
    templateId: typeof data.templateId === 'string' ? data.templateId : '',
    templateVersion: typeof data.templateVersion === 'number' ? data.templateVersion : 0,
    distribution: asDistribution(data.distribution),
    candidatePostIds: Array.isArray(data.candidatePostIds)
      ? data.candidatePostIds.filter((x): x is string => typeof x === 'string')
      : [],
    startAt:
      data.startAt === null ? null : data.startAt === undefined ? null : tsToIso(data.startAt),
    endAt: data.endAt === null ? null : data.endAt === undefined ? null : tsToIso(data.endAt),
    status: data.status === 'published' || data.status === 'closed' ? data.status : 'draft',
    createdAt: tsToIso(data.createdAt),
    createdBy: typeof data.createdBy === 'string' ? data.createdBy : '',
    updatedAt: tsToIso(data.updatedAt),
    updatedBy: typeof data.updatedBy === 'string' ? data.updatedBy : '',
  };
  const rc = asRandomConstraints(data.randomConstraints);
  if (rc) out.randomConstraints = rc;
  const perUser = asPerUserAssignments(data.perUserAssignments);
  if (perUser) out.perUserAssignments = perUser;
  if (data.sessionId !== undefined) {
    out.sessionId = typeof data.sessionId === 'string' ? data.sessionId : null;
  }
  if (Array.isArray(data.sessionIds)) {
    out.sessionIds = data.sessionIds.filter((x): x is string => typeof x === 'string');
  }
  // 生成メタ (2026-09-04): all_same × sameSessionOnly の割当解決 / 予約由来の表示分岐に使う。
  const gc = asGenerationConstraints(data.generationConstraints);
  if (gc) out.generationConstraints = gc;
  if (data.sourcePlanId !== undefined) {
    out.sourcePlanId = typeof data.sourcePlanId === 'string' ? data.sourcePlanId : null;
  }
  if (data.sourceSessionId !== undefined) {
    out.sourceSessionId = typeof data.sourceSessionId === 'string' ? data.sourceSessionId : null;
  }
  if (
    data.generationState === 'running' ||
    data.generationState === 'complete' ||
    data.generationState === 'failed'
  ) {
    out.generationState = data.generationState;
  }
  return out;
}

/** 現セッションが set の対象に含まれているか (sessionIds が空/undefined → 地区全体扱い)。 */
export function matchesCurrentSession(
  set: AiVerificationAssignmentSet,
  currentSessionId: string | null,
): boolean {
  const ids = set.sessionIds;
  if (!ids || ids.length === 0) {
    // legacy sessionId 単一指定 (deprecated)
    if (set.sessionId) {
      return currentSessionId === set.sessionId;
    }
    return true; // 地区全体
  }
  if (!currentSessionId) return false;
  return ids.includes(currentSessionId);
}

export function useAssignmentSets(
  districtId: string,
  currentSessionId: string | null,
): AssignmentSetsState {
  const [state, setState] = useState<AssignmentSetsState>({
    status: 'loading',
    items: [],
    error: null,
  });

  useEffect(() => {
    if (!districtId) {
      setState({ status: 'error', items: [], error: new Error('districtId is required') });
      return;
    }
    const q = query(
      collection(db, 'districts', districtId, 'aiVerificationAssignmentSets'),
      where('status', '==', 'published'),
    );
    const unsub: Unsubscribe = onSnapshot(
      q,
      (snap) => {
        const all = snap.docs.map(mapAssignmentSet);
        const filtered = all.filter((s) => matchesCurrentSession(s, currentSessionId));
        setState({ status: 'ready', items: filtered, error: null });
      },
      (error) => setState({ status: 'error', items: [], error }),
    );
    return () => unsub();
  }, [districtId, currentSessionId]);

  return state;
}
